import { FormEvent, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Badge from '../components/common/Badge';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { NativeSelect, TextArea } from '../components/common/FormField';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { queryKeys } from '../lib/queryClient';
import { api, errorMessage } from '../services/api';
import { Client } from '../types';
import { blankToNull } from '../utils/format';
import {
  CITY_PATTERN,
  compact,
  focusFirstError,
  GST_STATE_CODES,
  GSTIN_PATTERN,
  hasErrors,
  INDIAN_STATES,
  MAX_PAYMENT_TERMS_DAYS,
  serverFieldErrors,
  v,
} from '../utils/validation';

interface FormState {
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  pinCode: string;
  gstNumber: string;
  panNumber: string;
  paymentTermsDays: string;
  notes: string;
}

const EMPTY: FormState = {
  companyName: '', contactPerson: '', email: '', phone: '', address: '', city: '', state: '',
  pinCode: '', gstNumber: '', panNumber: '', paymentTermsDays: '', notes: '',
};

function toForm(c: Client): FormState {
  return {
    companyName: c.companyName,
    contactPerson: c.contactPerson ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    address: c.address ?? '',
    city: c.city ?? '',
    state: c.state ?? '',
    pinCode: c.pinCode ?? '',
    gstNumber: c.gstNumber ?? '',
    panNumber: c.panNumber ?? '',
    paymentTermsDays: c.paymentTermsDays != null ? String(c.paymentTermsDays) : '',
    notes: c.notes ?? '',
  };
}

export default function ClientsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('clients:manage');

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Client | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const { data: clients = [], isLoading } = useQuery({
    queryKey: queryKeys.clients.all(),
    queryFn: () => api.get<Client[]>('/clients'),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editing ? api.patch(`/clients/${editing.id}`, payload) : api.post('/clients', payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() }),
  });

  const toggleMutation = useMutation({
    mutationFn: (c: Client) => api.patch(`/clients/${c.id}`, { isActive: !c.isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.clients.all() }),
  });

  const displayed = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) =>
      [c.companyName, c.contactPerson, c.email, c.gstNumber, c.city].some((field) => field?.toLowerCase().includes(q)),
    );
  }, [clients, search]);

  function open(client?: Client) {
    setEditing(client ?? null);
    setForm(client ? toForm(client) : EMPTY);
    setErrors({});
    setShowModal(true);
  }

  function setField(key: keyof FormState, value: string) {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => (p[key] ? { ...p, [key]: undefined } : p));
  }

  function validate(): boolean {
    const name = form.companyName.trim().toLowerCase();
    const duplicate = clients.some((c) => c.id !== editing?.id && c.companyName.toLowerCase() === name);
    const e = compact<Record<keyof FormState, string | undefined>>({
      companyName: v.businessName(form.companyName, 'Company name', true) ?? (duplicate ? 'A client with this name already exists' : undefined),
      contactPerson: v.personName(form.contactPerson, 'Contact person'),
      email: v.email(form.email),
      phone: v.phone(form.phone),
      address: v.text(form.address, 'Address', { min: 5, max: 500 }),
      city: v.text(form.city, 'City', { min: 2, max: 100, pattern: CITY_PATTERN, patternMessage: 'City can only contain letters and spaces' }),
      state: v.stateMatchesGstin(form.state, form.gstNumber),
      pinCode: v.pin(form.pinCode),
      gstNumber: v.gstin(form.gstNumber),
      panNumber: v.pan(form.panNumber) ?? v.panMatchesGstin(form.panNumber, form.gstNumber),
      paymentTermsDays: v.wholeNumber(form.paymentTermsDays, 'Payment terms', { min: 0, max: MAX_PAYMENT_TERMS_DAYS }),
      notes: v.text(form.notes, 'Notes', { max: 1000 }),
    });
    setErrors(e);
    if (hasErrors(e)) focusFirstError();
    return !hasErrors(e);
  }

  // Typing a GSTIN fills the state and PAN it encodes, when those are still blank.
  function onGstinChange(raw: string) {
    const gstin = raw.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setForm((p) => {
      const next = { ...p, gstNumber: gstin };
      if (GSTIN_PATTERN.test(gstin)) {
        if (!p.state && GST_STATE_CODES[gstin.slice(0, 2)]) next.state = GST_STATE_CODES[gstin.slice(0, 2)];
        if (!p.panNumber) next.panNumber = gstin.slice(2, 12);
      }
      return next;
    });
    setErrors((p) => ({ ...p, gstNumber: undefined, state: undefined, panNumber: undefined }));
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!validate()) return;
    const payload = blankToNull({
      ...form,
      paymentTermsDays: form.paymentTermsDays === '' ? '' : (Number(form.paymentTermsDays) as any),
    });
    try {
      await saveMutation.mutateAsync(payload);
      showToast(editing ? 'Client updated' : 'Client added', 'success');
      setShowModal(false);
    } catch (err) {
      setErrors(serverFieldErrors(err, Object.keys(EMPTY) as (keyof FormState)[]));
      showToast(errorMessage(err, 'Failed to save client'), 'error');
    }
  }

  const columns = [
    {
      key: 'companyName',
      label: 'Client',
      render: (_: any, row: Client) => (
        <div>
          <span className="text-sm font-semibold text-ink-900">{row.companyName}</span>
          {row.contactPerson && <span className="block text-xs text-ink-500">{row.contactPerson}</span>}
        </div>
      ),
    },
    {
      key: 'contact',
      label: 'Contact',
      render: (_: any, row: Client) => (
        <div className="text-xs text-ink-600">
          <div>{row.email ?? '—'}</div>
          <div>{row.phone ?? ''}</div>
        </div>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      render: (_: any, row: Client) => (
        <span className="text-sm text-ink-600">{[row.city, row.state].filter(Boolean).join(', ') || '—'}</span>
      ),
    },
    {
      key: 'gstNumber',
      label: 'GSTIN',
      render: (_: any, row: Client) => <span className="font-mono text-xs text-ink-700">{row.gstNumber ?? '—'}</span>,
    },
    {
      key: 'isActive',
      label: 'Status',
      render: (_: any, row: Client) =>
        row.isActive ? <Badge variant="success" size="sm" rounded>Active</Badge> : <Badge variant="secondary" size="sm" rounded>Inactive</Badge>,
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '260px',
      render: (_: any, row: Client) => (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            className="px-3 py-1 bg-brand-50 text-brand-700 text-xs rounded hover:bg-brand-100 whitespace-nowrap"
            onClick={() => navigate(`/billing-config?clientId=${row.id}`)}
          >
            <i className="fas fa-sliders mr-1" />
            Billing
          </button>
          {canManage && (
            <>
              <button
                type="button"
                className="px-3 py-1 bg-ink-100 text-ink-700 text-xs rounded hover:bg-ink-200 whitespace-nowrap"
                onClick={() => open(row)}
              >
                <i className="fas fa-pen mr-1" />
                Edit
              </button>
              <button
                type="button"
                className="px-3 py-1 bg-white border border-ink-200 text-ink-600 text-xs rounded hover:bg-ink-50 whitespace-nowrap"
                disabled={toggleMutation.isPending}
                onClick={() =>
                  toggleMutation.mutate(row, {
                    onError: (err) => showToast(errorMessage(err), 'error'),
                  })
                }
              >
                {row.isActive ? 'Deactivate' : 'Activate'}
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Clients"
          subtitle={`Companies you bill — ${clients.filter((c) => c.isActive).length} active`}
          actions={
            canManage && (
              <Button variant="primary" icon="fa-plus" onClick={() => open()}>
                Add Client
              </Button>
            )
          }
        />

        <div className="mb-4 max-w-md">
          <Input
            icon="fa-search"
            placeholder="Search by name, contact, GSTIN, city…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-ink-200 overflow-visible lg:overflow-hidden">
          <div className="px-1 py-3 lg:p-0">
            <ResponsiveTable
              columns={columns}
              data={displayed}
              isLoading={isLoading}
              height="calc(100vh - 280px)"
              emptyMessage="No clients yet — add your first client to start billing"
            />
          </div>
        </div>

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editing ? 'Edit Client' : 'Add Client'}
          size="lg"
          footer={
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit as any} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : 'Save Client'}
              </Button>
            </div>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Input label="Company Name" required value={form.companyName} maxLength={255}
              onChange={(e) => setField('companyName', e.target.value)} error={errors.companyName} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Contact Person" value={form.contactPerson} maxLength={120}
                onChange={(e) => setField('contactPerson', e.target.value.replace(/[^A-Za-z .'-]/g, ''))} error={errors.contactPerson} />
              <Input label="Phone" type="tel" value={form.phone} maxLength={14}
                onChange={(e) => setField('phone', e.target.value.replace(/[^0-9+\s-]/g, ''))} error={errors.phone}
                helperText="10-digit mobile, e.g. 9876543210" />
              <Input label="Email" type="email" value={form.email} maxLength={255}
                onChange={(e) => setField('email', e.target.value.trim())} error={errors.email} />
              <Input label="Payment Terms (days)" type="number" decimals={0} value={form.paymentTermsDays} maxLength={3}
                onChange={(e) => setField('paymentTermsDays', e.target.value)}
                error={errors.paymentTermsDays} helperText="0-365. Leave blank to use the company default" />
            </div>
            <Input label="Address" value={form.address} maxLength={500}
              onChange={(e) => setField('address', e.target.value)} error={errors.address} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input label="City" value={form.city} maxLength={100}
                onChange={(e) => setField('city', e.target.value.replace(/[^A-Za-z .'-]/g, ''))} error={errors.city} />
              <NativeSelect label="State" value={form.state} onChange={(val) => setField('state', val)} error={errors.state}
                placeholder="Select state…" options={INDIAN_STATES.map((s) => ({ value: s, label: s }))} />
              <Input label="PIN Code" type="number" decimals={0} value={form.pinCode} maxLength={6}
                onChange={(e) => setField('pinCode', e.target.value)} error={errors.pinCode} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="GSTIN" value={form.gstNumber} maxLength={15}
                onChange={(e) => onGstinChange(e.target.value)} error={errors.gstNumber}
                helperText="15 characters — decides CGST+SGST vs IGST on invoices" />
              <Input label="PAN" value={form.panNumber} maxLength={10}
                onChange={(e) => setField('panNumber', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} error={errors.panNumber} />
            </div>
            <TextArea label="Notes" value={form.notes} onChange={(val) => setField('notes', val)} maxLength={1000} error={errors.notes} />
          </form>
        </Modal>
      </div>
    </Layout>
  );
}
